// ---------------------------------------------------------------------------
// CommentsFeedPanel -- Comments insights dashboard (Enhanced)
// ---------------------------------------------------------------------------
// Layout: Metric cards → Two-column (comments left, analytics right)
// Analytics sidebar: Sentiment → Emotions → Hashtags → Emojis → Languages → Word Cloud
// Comments: sortable by likes/sentiment/date, sentiment-colored left border
// ---------------------------------------------------------------------------

import React, { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import {
  MessageSquare,
  Heart,
  AlertTriangle,
  ShieldAlert,
  TrendingUp,
  Users,
  ExternalLink,
} from 'lucide-react';
import { getPlatformIcon } from '@/components/v11/shared/platformIcons';
import { buildPostUrl } from '@/lib/socialUrlBuilder';
import type { CommentsSummary, EnrichedComment, ActivityHeatmapCell } from './types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CommentsFeedPanelProps {
  readonly data: CommentsSummary | null;
  readonly loading: boolean;
  readonly className?: string;
}

type CommentSortMode = 'likes' | 'sentiment' | 'date';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SORT_PILLS: readonly { readonly id: CommentSortMode; readonly label: string }[] = [
  { id: 'likes', label: 'By Likes' },
  { id: 'sentiment', label: 'By Sentiment' },
  { id: 'date', label: 'By Date' },
];

const EMOTION_COLORS: Readonly<Record<string, string>> = {
  joy: '#34d399',
  excitement: '#22d3ee',
  love: '#f472b6',
  admiration: '#a78bfa',
  hope: '#60a5fa',
  pride: '#fbbf24',
  gratitude: '#2dd4bf',
  frustration: '#f87171',
  annoyance: '#fb923c',
  confusion: '#fbbf24',
  disappointment: '#fb7185',
  sadness: '#818cf8',
  hatred: '#ef4444',
  surprise: '#e879f9',
};

const HASHTAG_COLORS = [
  '#60a5fa',
  '#3b82f6',
  '#93c5fd',
  '#2563eb',
  '#38bdf8',
  '#818cf8',
  '#6366f1',
  '#a5b4fc',
  '#22d3ee',
  '#7dd3fc',
];

const LANG_COLORS = ['#60a5fa', '#22d3ee', '#fbbf24'];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function CommentsFeedPanel({ data, loading, className }: CommentsFeedPanelProps) {
  const [sortMode, setSortMode] = useState<CommentSortMode>('likes');

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-v11-text-muted">
          {loading ? 'Analyzing comments...' : 'No data available'}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Metric Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          icon={<MessageSquare className="h-4 w-4 text-blue-400" />}
          label="Total Comments"
          value={data.totalComments.toLocaleString()}
        />
        <MetricCard
          icon={<TrendingUp className="h-4 w-4 text-yellow-400" />}
          label="Avg Sentiment"
          value={data.avgSentiment.toFixed(2)}
          subtitle={getSentimentLabel(data.avgSentiment)}
          accentColor={getSentimentColor(data.avgSentiment)}
        />
        <MetricCard
          icon={<AlertTriangle className="h-4 w-4 text-red-400" />}
          label="Toxic Rate"
          value={`${(data.toxicRate * 100).toFixed(1)}%`}
          subtitle={`${Math.round(data.toxicRate * data.totalComments).toLocaleString()} comments`}
        />
        <MetricCard
          icon={<ShieldAlert className="h-4 w-4 text-orange-400" />}
          label="Spam Rate"
          value={`${(data.spamRate * 100).toFixed(1)}%`}
          subtitle={`${Math.round(data.spamRate * data.totalComments).toLocaleString()} comments`}
        />
      </div>

      {/* Two-column layout: Left = comments (2fr) | Right = analytics (1fr) */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4">
        {/* Left — Top Comments with sort pills */}
        <div className="rounded-xl border border-v11-border bg-v11-surface p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-purple-400" />
              <span className="text-sm font-semibold text-v11-text-primary">Top Comments</span>
            </div>
            {/* Sort Pills */}
            <div className="flex items-center gap-1">
              {SORT_PILLS.map(pill => (
                <button
                  key={pill.id}
                  onClick={() => setSortMode(pill.id)}
                  className={cn(
                    'text-[11px] px-2.5 py-1 rounded-full font-medium transition-all',
                    sortMode === pill.id
                      ? 'bg-blue-500/15 text-blue-400 ring-1 ring-blue-500/30'
                      : 'text-v11-text-muted hover:text-v11-text-secondary hover:bg-v11-bg'
                  )}
                >
                  {pill.label}
                </button>
              ))}
            </div>
          </div>
          <SortedCommentsList comments={data.topComments} sortMode={sortMode} />
        </div>

        {/* Right — analytics stack (reordered: Sentiment → Emotions → Hashtags → Emojis → Languages → Word Cloud) */}
        <div className="space-y-4 lg:sticky lg:top-4 lg:self-start">
          {/* Sentiment (promoted to top) */}
          <div className="rounded-xl border border-v11-border bg-v11-surface p-5">
            <div className="text-sm font-semibold text-v11-text-primary mb-3">Sentiment</div>
            <SentimentBar buckets={data.sentimentBuckets} total={data.totalComments} />
          </div>

          {/* Emotions */}
          <div className="rounded-xl border border-v11-border bg-v11-surface p-5">
            <div className="text-sm font-semibold text-v11-text-primary mb-3">Emotions</div>
            <CompactEmotions emotions={data.emotions.slice(0, 6)} total={data.totalComments} />
          </div>

          {/* Hashtags (top 10 list) */}
          {data.topHashtags.length > 0 && (
            <div className="rounded-xl border border-v11-border bg-v11-surface p-5">
              <div className="text-sm font-semibold text-v11-text-primary mb-3">Top Hashtags</div>
              <RankedList
                items={data.topHashtags
                  .slice(0, 10)
                  .map(h => ({ label: `#${h.hashtag}`, count: h.count }))}
                barColor="#3b82f6"
              />
            </div>
          )}

          {/* Top Emojis */}
          <div className="rounded-xl border border-v11-border bg-v11-surface p-5">
            <div className="text-sm font-semibold text-v11-text-primary mb-4">Top Emojis</div>
            {data.topEmojis.length > 0 ? (
              <EmojiGrid emojis={data.topEmojis} />
            ) : (
              <div className="text-sm text-v11-text-muted py-4 text-center">
                No emoji data available
              </div>
            )}
          </div>

          {/* Languages */}
          <div className="rounded-xl border border-v11-border bg-v11-surface p-5">
            <div className="text-sm font-semibold text-v11-text-primary mb-3">Languages</div>
            <CompactLanguages languages={data.languages.slice(0, 5)} total={data.totalComments} />
          </div>

          {/* Activity Heatmap */}
          {data.activityHeatmap && data.activityHeatmap.length > 0 && (
            <div className="rounded-xl border border-v11-border bg-v11-surface p-5">
              <div className="text-sm font-semibold text-v11-text-primary mb-3">Peak Activity</div>
              <ActivityHeatmap data={data.activityHeatmap} />
            </div>
          )}

          {/* Top Words (top 10 list) */}
          {data.topWords.length > 0 && (
            <div className="rounded-xl border border-v11-border bg-v11-surface p-5">
              <div className="text-sm font-semibold text-v11-text-primary mb-3">Top Words</div>
              <RankedList
                items={data.topWords.slice(0, 10).map(w => ({ label: w.word, count: w.count }))}
                barColor="#8b5cf6"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SortedCommentsList — client-side sort over the top 20 comments
// ---------------------------------------------------------------------------

function SortedCommentsList({
  comments,
  sortMode,
}: {
  readonly comments: readonly EnrichedComment[];
  readonly sortMode: CommentSortMode;
}) {
  const sorted = useMemo(() => {
    const arr = [...comments];
    switch (sortMode) {
      case 'likes':
        return arr.sort((a, b) => b.comment_likes_count - a.comment_likes_count);
      case 'sentiment':
        return arr.sort((a, b) => (b.sentiment ?? 0) - (a.sentiment ?? 0));
      case 'date':
        return arr.sort((a, b) => {
          const da = a.comment_date ? new Date(a.comment_date).getTime() : 0;
          const db = b.comment_date ? new Date(b.comment_date).getTime() : 0;
          return db - da;
        });
      default:
        return arr;
    }
  }, [comments, sortMode]);

  return (
    <div className="space-y-3">
      {sorted.map(comment => (
        <TopCommentCard key={comment.id} comment={comment} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// MetricCard
// ---------------------------------------------------------------------------

function MetricCard({
  icon,
  label,
  value,
  subtitle,
  accentColor,
}: {
  readonly icon: React.ReactNode;
  readonly label: string;
  readonly value: string;
  readonly subtitle?: string;
  readonly accentColor?: string;
}) {
  return (
    <div className="rounded-xl border border-v11-border bg-v11-surface p-4">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-xs text-v11-text-muted uppercase tracking-wider">{label}</span>
      </div>
      <div
        className="text-2xl font-bold text-v11-text-primary"
        style={accentColor ? { color: accentColor } : undefined}
      >
        {value}
      </div>
      {subtitle && <div className="text-xs text-v11-text-muted mt-1">{subtitle}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// HashtagPills — horizontal pill cloud (replaces old tag cloud)
// ---------------------------------------------------------------------------

function HashtagPills({ items }: { readonly items: readonly { text: string; count: number }[] }) {
  if (items.length === 0) return null;

  const maxCount = items[0]?.count ?? 1;

  return (
    <div className="space-y-1.5">
      {items.map((item, i) => {
        const pct = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
        const color = HASHTAG_COLORS[i % HASHTAG_COLORS.length];
        const isTop3 = i < 3;

        return (
          <div key={item.text} className="group relative">
            <div className="flex items-center gap-2.5">
              {/* Rank */}
              <span
                className="text-[10px] tabular-nums w-4 text-right flex-shrink-0"
                style={{ color, opacity: isTop3 ? 1 : 0.5, fontWeight: isTop3 ? 700 : 400 }}
              >
                {i + 1}
              </span>
              {/* Bar + label */}
              <div className="flex-1 min-w-0">
                <div className="relative h-7 rounded-md overflow-hidden bg-v11-bg/60">
                  <div
                    className="absolute inset-y-0 left-0 rounded-md transition-all duration-500"
                    style={{
                      width: `${Math.max(pct, 4)}%`,
                      background: `linear-gradient(90deg, ${color}${isTop3 ? '30' : '18'}, ${color}${isTop3 ? '12' : '08'})`,
                      borderRight: `2px solid ${color}${isTop3 ? '60' : '30'}`,
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-between px-2.5">
                    <span
                      className="text-xs truncate"
                      style={{
                        color,
                        fontWeight: isTop3 ? 600 : 500,
                        opacity: isTop3 ? 1 : 0.7,
                      }}
                    >
                      {item.text}
                    </span>
                    <span className="text-[10px] tabular-nums text-v11-text-muted flex-shrink-0 ml-2">
                      {formatShort(item.count)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Word Cloud — d3-cloud powered via @isoterik/react-word-cloud
// ---------------------------------------------------------------------------

const WORD_COLORS = [
  '#818cf8',
  '#a78bfa',
  '#c084fc',
  '#e879f9',
  '#f472b6',
  '#fb923c',
  '#fbbf24',
  '#34d399',
  '#22d3ee',
  '#60a5fa',
  '#f87171',
  '#2dd4bf',
];

function RankedList({
  items,
  barColor,
}: {
  readonly items: readonly { label: string; count: number }[];
  readonly barColor: string;
}) {
  if (items.length === 0) return null;

  const maxCount = items[0]?.count ?? 1;

  return (
    <div className="space-y-2">
      {items.map((item, i) => {
        const barWidth = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
        return (
          <div key={item.label}>
            <div className="flex items-center justify-between mb-0.5">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs text-v11-text-muted w-4 text-right font-semibold">
                  {i + 1}
                </span>
                <span className="text-sm text-v11-text-primary truncate font-medium">
                  {item.label}
                </span>
              </div>
              <span className="text-xs text-v11-text-muted tabular-nums ml-2 shrink-0">
                {formatShort(item.count)}
              </span>
            </div>
            <div className="ml-6 h-1.5 rounded-full bg-v11-bg overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${barWidth}%`, backgroundColor: barColor }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EmojiGrid
// ---------------------------------------------------------------------------

function EmojiGrid({ emojis }: { readonly emojis: readonly { emoji: string; count: number }[] }) {
  return (
    <div className="grid grid-cols-4 gap-2.5">
      {emojis.slice(0, 12).map(({ emoji, count }) => (
        <div key={emoji} className="flex flex-col items-center gap-1 rounded-lg bg-v11-bg p-2">
          <span className="text-xl">{emoji}</span>
          <span className="text-[10px] text-v11-text-muted tabular-nums">{formatShort(count)}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SentimentBar
// ---------------------------------------------------------------------------

function SentimentBar({
  buckets,
  total,
}: {
  readonly buckets: {
    readonly positive: number;
    readonly neutral: number;
    readonly negative: number;
  };
  readonly total: number;
}) {
  const pct = (n: number) => (total > 0 ? ((n / total) * 100).toFixed(1) : '0');

  return (
    <div className="space-y-2.5">
      <div className="flex h-5 rounded-full overflow-hidden bg-v11-bg">
        {buckets.positive > 0 && (
          <div
            className="transition-all"
            style={{
              width: `${pct(buckets.positive)}%`,
              background: 'linear-gradient(90deg, #10b981, #34d399)',
            }}
            title={`Positive: ${pct(buckets.positive)}%`}
          />
        )}
        {buckets.neutral > 0 && (
          <div
            className="transition-all"
            style={{
              width: `${pct(buckets.neutral)}%`,
              background: 'linear-gradient(90deg, #eab308, #fbbf24)',
            }}
            title={`Neutral: ${pct(buckets.neutral)}%`}
          />
        )}
        {buckets.negative > 0 && (
          <div
            className="transition-all"
            style={{
              width: `${pct(buckets.negative)}%`,
              background: 'linear-gradient(90deg, #ef4444, #f87171)',
            }}
            title={`Negative: ${pct(buckets.negative)}%`}
          />
        )}
      </div>
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-1.5">
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ background: 'linear-gradient(135deg, #10b981, #34d399)' }}
          />
          <span className="text-v11-text-muted">Positive {pct(buckets.positive)}%</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ background: 'linear-gradient(135deg, #eab308, #fbbf24)' }}
          />
          <span className="text-v11-text-muted">Neutral {pct(buckets.neutral)}%</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ background: 'linear-gradient(135deg, #ef4444, #f87171)' }}
          />
          <span className="text-v11-text-muted">Negative {pct(buckets.negative)}%</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// TopCommentCard — with sentiment-colored left border
// ---------------------------------------------------------------------------

function TopCommentCard({ comment }: { readonly comment: EnrichedComment }) {
  const platformIcon = getPlatformIcon(comment.platform);
  const sentimentBorderColor = getSentimentColor(comment.sentiment ?? 5);

  return (
    <div
      className="rounded-lg border border-v11-border bg-v11-bg p-4 hover:border-v11-text-muted/30 transition-colors"
      style={{ borderLeftWidth: 3, borderLeftColor: sentimentBorderColor }}
    >
      <div className="flex items-center gap-3 mb-2">
        {comment.profile?.profile_pic_url ? (
          <img
            src={comment.profile.profile_pic_url}
            alt={comment.author}
            className="h-8 w-8 rounded-full object-cover flex-shrink-0"
            onError={e => {
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <div className="h-8 w-8 rounded-full bg-v11-surface flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-bold text-v11-text-muted">
              {comment.author.charAt(0).toUpperCase()}
            </span>
          </div>
        )}
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {platformIcon && (
            <span
              className="flex-shrink-0"
              style={{ color: platformIcon.color }}
              title={platformIcon.name}
            >
              {platformIcon.icon}
            </span>
          )}
          <span className="text-sm font-semibold text-v11-text-primary truncate">
            @{comment.profile?.username ?? comment.author}
          </span>
          {comment.profile && comment.profile.followers_count > 0 && (
            <span className="text-xs text-v11-text-muted flex-shrink-0">
              {formatShort(comment.profile.followers_count)} followers
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {comment.toxic && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              Toxic
            </span>
          )}
          {comment.emotion && (
            <span
              className={cn(
                'text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize',
                getEmotionChipStyle(comment.emotion)
              )}
            >
              {comment.emotion}
            </span>
          )}
        </div>
      </div>
      <p className="text-sm text-v11-text-secondary mb-2 line-clamp-3">{comment.text}</p>

      {/* Post context — where was this comment made */}
      {comment.post?.author &&
        (() => {
          const postUrl =
            comment.platform && comment.shortcode
              ? buildPostUrl(comment.platform, comment.shortcode, comment.post?.author ?? '')
              : null;
          const Wrapper = postUrl ? 'a' : 'div';
          const wrapperProps = postUrl
            ? { href: postUrl, target: '_blank' as const, rel: 'noopener noreferrer' }
            : {};
          return (
            <Wrapper
              {...wrapperProps}
              className={cn(
                'mb-2 px-3 py-2 rounded-lg bg-v11-surface/50 border border-v11-border/50 block',
                postUrl &&
                  'hover:bg-v11-surface-hover hover:border-v11-border transition-colors cursor-pointer'
              )}
            >
              <div className="flex items-center gap-2 text-[11px] text-v11-text-muted">
                <span className="font-medium text-v11-text-secondary">
                  on @{comment.post.author}
                </span>
                {comment.post.caption && (
                  <span className="truncate flex-1 italic">
                    &quot;{comment.post.caption.slice(0, 80)}
                    {comment.post.caption.length > 80 ? '...' : ''}&quot;
                  </span>
                )}
                {comment.post.likes_count > 0 && (
                  <span className="flex items-center gap-0.5 flex-shrink-0">
                    <Heart className="h-2.5 w-2.5" />
                    {formatShort(comment.post.likes_count)}
                  </span>
                )}
                {comment.post.comments_count > 0 && (
                  <span className="flex items-center gap-0.5 flex-shrink-0">
                    <MessageSquare className="h-2.5 w-2.5" />
                    {formatShort(comment.post.comments_count)}
                  </span>
                )}
                {postUrl && <ExternalLink className="h-3 w-3 flex-shrink-0 text-v11-text-muted" />}
              </div>
            </Wrapper>
          );
        })()}

      <div className="flex items-center justify-between text-xs text-v11-text-muted">
        <div className="flex items-center gap-3">
          {comment.comment_likes_count > 0 && (
            <span className="flex items-center gap-1">
              <Heart className="h-3 w-3 text-red-400" />
              <span className="font-semibold text-v11-text-secondary">
                {comment.comment_likes_count.toLocaleString()}
              </span>
            </span>
          )}
          {comment.sentiment != null && (
            <span
              className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
              style={{
                backgroundColor: `${getSentimentColor(comment.sentiment)}15`,
                color: getSentimentColor(comment.sentiment),
              }}
            >
              {comment.sentiment.toFixed(1)}
            </span>
          )}
          {comment.language && (
            <span className="px-1.5 py-0.5 rounded bg-v11-surface text-v11-text-muted">
              {comment.language}
            </span>
          )}
        </div>
        {comment.comment_date && <span>{new Date(comment.comment_date).toLocaleDateString()}</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CompactEmotions — minimal inline emotion bars
// ---------------------------------------------------------------------------

function CompactEmotions({
  emotions,
  total,
}: {
  readonly emotions: readonly { emotion: string; count: number }[];
  readonly total: number;
}) {
  if (emotions.length === 0) {
    return <div className="text-sm text-v11-text-muted text-center py-2">No data</div>;
  }
  const maxCount = emotions[0]?.count ?? 1;

  return (
    <div className="space-y-2">
      {emotions.map(em => {
        const pct = total > 0 ? (em.count / total) * 100 : 0;
        const barWidth = maxCount > 0 ? (em.count / maxCount) * 100 : 0;
        const color = EMOTION_COLORS[em.emotion.toLowerCase()] ?? '#60a5fa';
        return (
          <div key={em.emotion} className="flex items-center gap-2">
            <span className="text-xs text-v11-text-secondary w-24 truncate capitalize">
              {em.emotion}
            </span>
            <div className="flex-1 h-2 rounded-full bg-v11-bg overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${barWidth}%`,
                  background: `linear-gradient(90deg, ${color}, ${color}99)`,
                }}
              />
            </div>
            <span className="text-xs text-v11-text-muted tabular-nums w-12 text-right">
              {pct.toFixed(0)}%
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// CompactLanguages — minimal inline language bars
// ---------------------------------------------------------------------------

function CompactLanguages({
  languages,
  total,
}: {
  readonly languages: readonly { language: string; count: number }[];
  readonly total: number;
}) {
  if (languages.length === 0) {
    return <div className="text-sm text-v11-text-muted text-center py-2">No data</div>;
  }
  const maxCount = languages[0]?.count ?? 1;

  return (
    <div className="space-y-2">
      {languages.map((lang, i) => {
        const pct = total > 0 ? (lang.count / total) * 100 : 0;
        const barWidth = maxCount > 0 ? (lang.count / maxCount) * 100 : 0;
        const color = LANG_COLORS[i] ?? '#60a5fa';
        return (
          <div key={lang.language} className="flex items-center gap-2">
            <span className="text-xs text-v11-text-secondary w-20 truncate">{lang.language}</span>
            <div className="flex-1 h-2 rounded-full bg-v11-bg overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${barWidth}%`,
                  background: `linear-gradient(90deg, ${color}, ${color}80)`,
                }}
              />
            </div>
            <span className="text-xs text-v11-text-muted tabular-nums w-12 text-right">
              {pct.toFixed(0)}%
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActivityHeatmap — day of week × hour grid
// ---------------------------------------------------------------------------

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

function ActivityHeatmap({ data }: { readonly data: readonly ActivityHeatmapCell[] }) {
  // Build a 7×24 matrix from sparse data
  const matrix = useMemo(() => {
    const grid: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
    let maxCount = 0;

    for (const cell of data) {
      grid[cell.day][cell.hour] = cell.count;
      if (cell.count > maxCount) maxCount = cell.count;
    }

    return { grid, maxCount };
  }, [data]);

  // Find peak hour
  const peak = useMemo(() => {
    let best = { day: 0, hour: 0, count: 0 };
    for (const cell of data) {
      if (cell.count > best.count) best = { day: cell.day, hour: cell.hour, count: cell.count };
    }
    return best;
  }, [data]);

  const formatHour = (h: number) => {
    if (h === 0) return '12a';
    if (h < 12) return `${h}a`;
    if (h === 12) return '12p';
    return `${h - 12}p`;
  };

  const getColor = (count: number) => {
    if (count === 0 || matrix.maxCount === 0) return 'rgba(96, 165, 250, 0.04)';
    const ratio = count / matrix.maxCount;
    if (ratio > 0.8) return 'rgba(59, 130, 246, 0.9)';
    if (ratio > 0.6) return 'rgba(59, 130, 246, 0.65)';
    if (ratio > 0.4) return 'rgba(59, 130, 246, 0.45)';
    if (ratio > 0.2) return 'rgba(59, 130, 246, 0.28)';
    if (ratio > 0.05) return 'rgba(59, 130, 246, 0.14)';
    return 'rgba(96, 165, 250, 0.06)';
  };

  // Show subset of hour labels to avoid clutter
  const hourLabels = [0, 3, 6, 9, 12, 15, 18, 21];

  return (
    <div className="space-y-3">
      {/* Grid */}
      <div className="overflow-x-auto">
        <div className="min-w-[300px]">
          {/* Hour labels row */}
          <div className="flex items-center mb-1">
            <div className="w-8 flex-shrink-0" />
            <div className="flex-1 flex">
              {Array.from({ length: 24 }, (_, h) => (
                <div key={h} className="flex-1 text-center">
                  {hourLabels.includes(h) && (
                    <span className="text-[9px] text-v11-text-muted tabular-nums">
                      {formatHour(h)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Day rows */}
          {DAY_LABELS.map((dayLabel, dayIdx) => (
            <div key={dayLabel} className="flex items-center gap-0.5 mb-0.5">
              <span className="text-[10px] text-v11-text-muted w-8 flex-shrink-0 text-right pr-1.5">
                {dayLabel}
              </span>
              <div className="flex-1 flex gap-[1px]">
                {Array.from({ length: 24 }, (_, h) => {
                  const count = matrix.grid[dayIdx][h];
                  return (
                    <div
                      key={h}
                      className="flex-1 aspect-square rounded-[2px] transition-colors"
                      style={{ backgroundColor: getColor(count) }}
                      title={`${dayLabel} ${formatHour(h)}: ${count.toLocaleString()} comments`}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Peak summary */}
      <div className="flex items-center justify-between text-[11px] text-v11-text-muted">
        <span>
          Peak:{' '}
          <span className="text-v11-text-secondary font-medium">
            {DAY_LABELS[peak.day]} {formatHour(peak.hour)}
          </span>{' '}
          ({peak.count.toLocaleString()} comments)
        </span>
        {/* Legend */}
        <div className="flex items-center gap-1">
          <span className="text-[9px]">Less</span>
          {[0.06, 0.14, 0.28, 0.45, 0.9].map((opacity, i) => (
            <div
              key={i}
              className="w-2.5 h-2.5 rounded-[2px]"
              style={{ backgroundColor: `rgba(59, 130, 246, ${opacity})` }}
            />
          ))}
          <span className="text-[9px]">More</span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatShort(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toString();
}

function getSentimentLabel(score: number): string {
  if (score >= 7) return 'Positive';
  if (score >= 4) return 'Neutral';
  return 'Negative';
}

function getSentimentColor(score: number): string {
  if (score >= 7) return '#10b981';
  if (score >= 4) return '#eab308';
  return '#ef4444';
}

function getEmotionChipStyle(emotion: string): string {
  const lower = emotion.toLowerCase();
  const POSITIVE = new Set([
    'joy',
    'excitement',
    'love',
    'admiration',
    'hope',
    'pride',
    'gratitude',
    'solidarity',
    'relief',
  ]);
  const NEGATIVE = new Set([
    'frustration',
    'annoyance',
    'confusion',
    'disappointment',
    'sadness',
    'hatred',
    'fear',
    'irony',
    'jealousy',
    'regret',
    'embarrassment',
  ]);
  if (POSITIVE.has(lower)) return 'bg-green-500/15 text-green-400';
  if (NEGATIVE.has(lower)) return 'bg-red-500/15 text-red-400';
  return 'bg-amber-500/15 text-amber-400';
}

export { CommentsFeedPanel };
export type { CommentsFeedPanelProps };
